/**
 * Tipos PRÓPRIOS do Portal do Cliente para a aba de Chamados.
 *
 * ⚠ Duplicados de propósito: `src/interfaces/Ticket.ts` e `src/services/ticketApi.ts` (tela interna
 * de chamados) estão sendo escritos por outro agente ao mesmo tempo. Este arquivo espelha só o
 * necessário do contrato (`openspec/changes/itsm-chamados-fundacao/contrato.md`, seção "Portal do
 * Cliente") — divergir do contrato é bug, mesmo compilando.
 *
 * O DTO do portal (`ClientTicketViewModel`) omite QueueId, AssigneeEmployeeId, comentários internos
 * e SlaPaused* — e não existe campo de prioridade aqui porque a prioridade nem mora no chamado: é
 * derivada (impacto × urgência) e aplicada na tarefa interna (D7). O cliente nunca vê prioridade.
 */

import { TicketAttachment } from "../../../../interfaces/Ticket";

// TicketState : Aberto=1, EmAtendimento=2, PendenteCliente=3, Resolvido=4, Fechado=5, Reaberto=6
export enum ClientTicketState {
  Aberto = 1,
  EmAtendimento = 2,
  PendenteCliente = 3,
  Resolvido = 4,
  Fechado = 5,
  Reaberto = 6,
}

// TicketImpact : UmaPessoa=1, UmaArea=2, Empresa=3
export enum ClientTicketImpact {
  UmaPessoa = 1,
  UmaArea = 2,
  Empresa = 3,
}

// TicketUrgency : PodeEsperar=1, AtrapalhaHoje=2, ParadoAgora=3
export enum ClientTicketUrgency {
  PodeEsperar = 1,
  AtrapalhaHoje = 2,
  ParadoAgora = 3,
}

/** Filtro do backend: `GET /api/ClientPortal/tickets?state=open|closed`. */
export type ClientTicketListFilter = "open" | "closed";

/**
 * Estado traduzido para o vocabulário do cliente — nunca "fila", "responsável", "nota interna" ou
 * "SLA pausado". O cliente vê o que aconteceu com o pedido dele, não como o time está operando.
 */
export const CLIENT_TICKET_STATE_LABELS: Record<ClientTicketState, string> = {
  [ClientTicketState.Aberto]: "Em análise",
  [ClientTicketState.EmAtendimento]: "Em andamento",
  [ClientTicketState.PendenteCliente]: "Aguardando você",
  [ClientTicketState.Resolvido]: "Resolvido",
  [ClientTicketState.Fechado]: "Fechado",
  [ClientTicketState.Reaberto]: "Reaberto",
};

/** Classe Tailwind (badge) por estado — cor carrega parte do significado sem precisar de legenda. */
export const CLIENT_TICKET_STATE_TONE: Record<ClientTicketState, string> = {
  [ClientTicketState.Aberto]:
    "bg-dv-blue-100 text-dv-blue-700 dark:bg-dv-blue-900/40 dark:text-dv-cyan-300",
  [ClientTicketState.EmAtendimento]:
    "bg-dv-blue-100 text-dv-blue-700 dark:bg-dv-blue-900/40 dark:text-dv-cyan-300",
  [ClientTicketState.PendenteCliente]:
    "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  [ClientTicketState.Resolvido]:
    "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  [ClientTicketState.Fechado]:
    "bg-gray-100 text-gray-700 dark:bg-navy-900 dark:text-gray-300",
  [ClientTicketState.Reaberto]:
    "bg-dv-magenta-100 text-dv-magenta-700 dark:bg-dv-magenta-900/40 dark:text-dv-magenta-300",
};

/**
 * Opções do formulário de abertura — em linguagem do cliente, nunca "prioridade" (D7). O texto
 * pergunta sobre o mundo dele (quem foi afetado, o quanto atrapalha); o servidor deriva a
 * prioridade da combinação das duas respostas.
 */
export const IMPACT_OPTIONS: { value: ClientTicketImpact; label: string; hint: string }[] = [
  { value: ClientTicketImpact.UmaPessoa, label: "Só eu", hint: "O problema atrapalha só o meu trabalho" },
  { value: ClientTicketImpact.UmaArea, label: "Uma área ou um time", hint: "Está atrapalhando um grupo de pessoas" },
  { value: ClientTicketImpact.Empresa, label: "A empresa toda", hint: "A operação inteira está parada por causa disso" },
];

export const URGENCY_OPTIONS: { value: ClientTicketUrgency; label: string; hint: string }[] = [
  { value: ClientTicketUrgency.PodeEsperar, label: "Dá para esperar", hint: "Não trava nada agora, mas precisa ser resolvido" },
  { value: ClientTicketUrgency.AtrapalhaHoje, label: "Está atrapalhando hoje", hint: "Dá para contornar, mas está causando problema" },
  { value: ClientTicketUrgency.ParadoAgora, label: "Estamos parados agora", hint: "Não conseguimos trabalhar enquanto isso não for resolvido" },
];

/**
 * Comentário público do chamado — o portal nunca envia nem recebe `isInternal` (o cliente não
 * escreve nota interna, e o backend já filtra as internas antes de montar `ClientTicketViewModel`).
 * `authorIsInternal` é diferente: é quem ESCREVEU esta mensagem pública — equipe ou o próprio
 * cliente — e vem do backend, não de um palpite do componente.
 */
export interface ClientTicketComment {
  id: number;
  authorName?: string;
  authorIsInternal: boolean;
  body: string;
  dtCreated: string;
  /** Anexos desta mensagem — o backend só devolve os públicos. */
  attachments?: TicketAttachment[];
}

/**
 * `ClientTicketViewModel` do contrato. `comments` só chega preenchido no detalhe (`GET /{id}`) —
 * não há endpoint de listagem de comentários no portal, o detalhe já "traz tudo" que o cliente
 * pode ver.
 */
/**
 * Categoria como o portal a recebe (`ClientTicketCategoryViewModel`): só id, nome e as filhas.
 *
 * **Não** traz fila padrão nem contagem de chamados — são decisões internas, e cada uma exposta
 * aqui é uma decisão que o cliente passa a cobrar. Mesma linha que o resto deste arquivo traça.
 */
export interface ClientTicketCategory {
  id: number;
  name: string;
  children: ClientTicketCategory[];
}

export interface ClientTicket {
  id: number;
  number: string;
  state: ClientTicketState;
  impact: ClientTicketImpact;
  urgency: ClientTicketUrgency;
  subject: string;
  description: string;
  openedAt: string;
  firstResponseAt?: string;
  resolvedAt?: string;
  closedAt?: string;
  /** Prazo de resolução prometido — o backend expõe justamente porque é o que o cliente tem direito de saber do SLA. */
  slaResolutionDueAt?: string;
  /** O que foi classificado (por ele ou pela triagem). Ver isto é o que permite ao cliente apontar
   *  quando a triagem entendeu outra coisa. */
  categoryId?: number | null;
  categoryPath?: string | null;
  comments?: ClientTicketComment[];
  /** Anexos do chamado (commentId nulo), só os públicos. */
  attachments?: TicketAttachment[];
}

export interface OpenTicketRequest {
  subject: string;
  description: string;
  impact: ClientTicketImpact;
  urgency: ClientTicketUrgency;
  /**
   * A folha escolhida. **Opcional, e isso é decisão**: obrigar quem já está com um problema a
   * acertar a taxonomia interna antes de pedir ajuda trocaria uma abertura de chamado por um
   * formulário. A triagem corrige o que vier errado.
   *
   * ⚠ Diferente do `clientId` (que este arquivo NUNCA manda, design D6), este id pode ir no corpo:
   * ele não decide quem o cliente é nem o que ele alcança — é um dado do pedido.
   */
  categoryId?: number | null;
}

/**
 * Resumo de SLA do cliente logado. Mesmo formato do interno (`TicketSlaCompliance`), mas o portal
 * nunca recebe `byAssignee` preenchido: quem atende é dado interno.
 */
export type { TicketSlaCompliance as ClientTicketSla } from "../../../../interfaces/Ticket";
